<script lang="ts">
const USP_COUNT = 7
</script>
<script setup lang="ts">
    import type { Benefit } from '~/app/auth/enums/TenantSubscriptionPlan';

    const props = withDefaults(
        defineProps<{
            benefit?: Benefit;
            permissionKey?: string;
            title?: string;
            description?: string;
            // Cross-family upsell (TDD-3522): hide the left price panel.
            crossFamily?: boolean;
        }>(),
        {
            benefit: undefined,
            permissionKey: undefined,
            title: undefined,
            description: undefined,
            crossFamily: false,
        }
    );

    const { t } = useTranslation();
    const { getUpsellPlanInfo, getUpsellPlanInfoForPermission, getUpsellTrackingPlan } = useUpsellAccess();

    // Tracking payload for the modal CTAs. `position` flags which CTA (left = pricing
    // panel, right = benefits panel) the user clicked. Plan info comes from the same
    // resolver as the restriction event so the properties stay consistent.
    const ctaTracking = (position: 'left' | 'right') => {
        const { planId, commercialName } = getUpsellTrackingPlan({ permissionKey: props.permissionKey, benefit: props.benefit });
        return {
            eventName: 'upsell_modal_cta_clicked',
            properties: { position, plan_id: planId, commercial_name: commercialName },
        };
    };

    const planInfo = computed(() => {
        if (props.permissionKey) return getUpsellPlanInfoForPermission(props.permissionKey);
        if (props.benefit) return getUpsellPlanInfo(props.benefit);
        return undefined;
    });
    const tier = computed(() => planInfo.value?.tier ?? 'essential');
    const planName = computed(() => planInfo.value?.name ?? 'ESSENTIAL');
    const price = computed(() => planInfo.value?.price ?? t(`upsell.${tier.value}.price`));

    // USPs are fixed per plan tier (essential / enterprise) — they don't vary by feature.
    // Only the title and description change (handled below via the title/description props).
    

    const usps = computed(() => {
        if (planInfo.value?.benefits?.length) return planInfo.value.benefits;
        return Array.from({ length: USP_COUNT }, (_, i) => t(`upsell.${tier.value}.usp_${i + 1}`));
    });

    const displayTitle = computed(() => props.title ?? t(`upsell.${tier.value}.title`));
    const displayDescription = computed(() => props.description ?? t(`upsell.${tier.value}.description`));
</script>

<template>
    <div class="flex items-stretch w-full max-w-[1000px] bg-white rounded-[15px] overflow-hidden shadow-xl mx-auto">
        <!-- Left panel — Illustration + plan + price (hidden on mobile). On a cross-family
             upsell the price is replaced by "on request" + contact note; the illustration,
             plan pill and CTA stay. -->
        <div class="relative w-[400px] shrink-0 overflow-hidden hidden md:flex flex-col items-center justify-center p-[10px]">
            <img
                src="~/assets/upsell-modal.png"
                alt=""
                class="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
            <div class="relative z-10 bg-white rounded-[10px] w-[300px] p-5 flex flex-col gap-[25px] items-center shadow-[0px_1px_5px_rgba(0,0,0,0.05)]">
                <CommonUpsellPlanPill :name="planName" />
                <div
                    v-if="!crossFamily"
                    class="text-center"
                >
                    <span class="text-4xl font-medium font-bricolage text-black">{{ price }}</span>
                    <span class="text-sm font-medium text-black"> / {{ $t('misc.month') }}</span>
                </div>
                <div
                    v-else
                    class="text-center"
                >
                    <span class="block text-3xl font-bold font-bricolage text-black">{{ $t('settings.subscription.overview.cards.on_request') }}</span>
                    <span class="text-sm font-inter text-branding-mid-grey">{{ $t('settings.subscription.overview.cards.on_request_note') }}</span>
                </div>
                <UiAction
                    :content="{ label: 'upsell.cta_discover', url: '/settings/subscription', type: 'main' }"
                    :tracking="ctaTracking('left')"
                    modifier="full"
                />
            </div>
        </div>

        <!-- Right panel — Title + benefits -->
        <div class="flex-1 flex flex-col items-center justify-center p-5">
            <div class="flex flex-col gap-[30px] max-w-[400px] w-full">
                <!-- Mobile-only plan badge -->
                <div class="flex justify-center md:hidden">
                    <CommonUpsellPlanPill
                        :name="planName"
                        size="md"
                    />
                </div>

                <h2 class="text-[28px] font-bold font-bricolage text-black text-center leading-tight">
                    {{ displayTitle }}
                </h2>
                <p class="text-sm font-inter text-black text-center tracking-[-0.14px]">
                    {{ displayDescription }}
                </p>
                <div class="flex flex-col gap-[10px]">
                    <div
                        v-for="usp in usps"
                        :key="usp"
                        class="flex items-start gap-2"
                    >
                        <i class="fa-solid fa-circle-check text-base text-[#53c59a] shrink-0" />
                        <span class="text-sm font-medium font-bricolage text-black">{{ usp }}</span>
                    </div>
                </div>
                <UiAction
                    :content="{ label: 'upsell.cta_discover', url: '/settings/subscription', type: 'main' }"
                    :tracking="ctaTracking('right')"
                    modifier="full"
                />
            </div>
        </div>
    </div>
</template>
